import { format } from 'date-fns';

import { type MemoFilter } from '../../../../../hooks/react-query/useMemoQuery';
import { type JobNoteFilterValues } from './jobNoteFiltersUtils';

/** Builds `dateRange` for memo list/report queries from job note filter UI state. */
export function buildJobNoteDateRange(filters: JobNoteFilterValues): string[] | undefined {
  if (!filters.startDate && !filters.endDate) return undefined;

  const startDate = filters.startDate ? format(filters.startDate, 'yyyy-MM-dd') : undefined;
  const endDate = filters.endDate ? format(filters.endDate, 'yyyy-MM-dd') : undefined;
  const startTime = filters.startTime;
  const endTime = filters.endTime;

  if (startDate && !endDate) {
    if (startTime) {
      const startTimeFormatted = startTime.replace(':', '-') + '-00';
      return ['>=', `${startDate} ${startTimeFormatted}`];
    }
    return ['>=', startDate];
  }

  if (!startDate && endDate) {
    if (endTime) {
      const endTimeFormatted = endTime.replace(':', '-') + '-00';
      return ['<=', `${endDate} ${endTimeFormatted}`];
    }
    return ['<=', endDate];
  }

  if (startDate && endDate) {
    if (startTime && endTime) {
      const startTimeFormatted = startTime.replace(':', '-') + '-00';
      const endTimeFormatted = endTime.replace(':', '-') + '-00';
      return ['>=', `${startDate} ${startTimeFormatted}`, '<=', `${endDate} ${endTimeFormatted}`];
    }
    return ['>=', startDate, '<=', endDate];
  }

  return undefined;
}

export type MemoListOrderDir = 'ASC' | 'DESC';

export type BuildJobNoteMemoFiltersParams = {
  filters: JobNoteFilterValues;
  jobId?: number;
  projectPrimaryEventId?: number;
  pageNumber: number;
  /** When true, fetch all matching memos (e.g. AI report). List uses false. */
  withoutLimit: boolean;
  orderBy?: string;
  orderDir?: MemoListOrderDir;
};

/**
 * Single source of truth for job/project note list and AI report memo queries.
 * Mirrors scope: project + optional job filter, or single job.
 */
export function buildJobNoteMemoFilters({
  filters,
  jobId,
  projectPrimaryEventId,
  pageNumber,
  withoutLimit,
  orderBy = 'created_date',
  orderDir = 'DESC',
}: BuildJobNoteMemoFiltersParams): MemoFilter {
  return {
    ...(projectPrimaryEventId
      ? filters.eventId > 0
        ? { eventId: filters.eventId }
        : { projectPrimaryEventId }
      : { eventId: jobId }),
    isActive: true,
    orderBy,
    orderDir,
    includeVideoFiles: true,
    includeUnsupportedMobileFiles: true,
    pageNumber,
    withoutLimit,
    userDepartmentId: filters.departmentId > 0 ? filters.departmentId : undefined,
    createdByIdList:
      filters.createdByIds && filters.createdByIds.length > 0
        ? filters.createdByIds.join(',')
        : undefined,
    dateRange: buildJobNoteDateRange(filters),
    description: filters.searchText ? `%${filters.searchText}%` : undefined,
  };
}
